import React, { useEffect, useRef, useState } from "react";

// Returns [ref, inView]; once visible it stays visible.
export const useInView = (options = { threshold: 0.15, rootMargin: "0px 0px -8% 0px" }) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (!("IntersectionObserver" in window)) {
      setInView(true);
      return undefined;
    }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        io.disconnect();
      }
    }, options);
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [ref, inView];
};

// Fades/slides its children in when scrolled into view.
export const Reveal = ({ as: Tag = "div", delay = 0, variant = "up", className = "", style, children, ...rest }) => {
  const [ref, inView] = useInView();
  const variantClass = variant === "fade" ? "reveal-fade" : variant === "scale" ? "reveal-scale" : "";
  return (
    <Tag
      ref={ref}
      className={`reveal ${variantClass} ${inView ? "is-visible" : ""} ${className}`}
      style={{ "--delay": `${delay}ms`, ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

// Counts from 0 to `value` once visible.
export const CountUp = ({ value, suffix = "", duration = 1400 }) => {
  const [ref, inView] = useInView();
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setN(value);
      return undefined;
    }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);
  return (
    <span ref={ref}>
      {n}
      {suffix}
    </span>
  );
};

// Thin progress bar at the top of the page.
export const ScrollProgress = () => {
  const [p, setP] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setP(h > 0 ? window.scrollY / h : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);
  return (
    <div
      aria-hidden="true"
      className="fixed inset-x-0 top-0 z-[60] h-[3px] origin-left bg-brand rtl:origin-right"
      style={{ transform: `scaleX(${p})` }}
    />
  );
};
